import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, CircularProgress } from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import ButtonBB from "../../../../../components/buttons/ButtonBB";
import {
  getAllCustomersWithClients,
  updateCustomerLeadRole,
  getLeadIntegrations,
} from "../../../../../../dao/api";
import ConfirmModal from "../../../../../components/ConfirmModal";
import SearchableSelect from "../../../../../components/SearchableSelect";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import PowerIcon from "@material-ui/icons/Power";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

const ROLE_GENERATOR = "LeadGenerator";
const ROLE_RECEIVER = "LeadReceiver";
const ROLE_STANDARD = "Standard";

const ROLE_LABELS = {
  [ROLE_GENERATOR]: "Лидогенератор",
  [ROLE_RECEIVER]: "Финансовая компания/Банк",
};

const INTERNAL_LABEL = "Только внутренняя передача лида";

// Поле поиска в колонке показываем только когда список заметно длинный.
const SEARCH_THRESHOLD = 0;

const roleMeta = (role) => {
  if (role === ROLE_GENERATOR) return { label: "Лидогенератор", bg: "#E9F5EE", fg: "#43A56C" };
  if (role === ROLE_RECEIVER) return { label: "Банк", bg: "#E9F2FC", fg: "#5195E6" };
  return null;
};

const useStyles = makeStyles((theme) => ({
  container: { padding: 0 },

  assignBar: {
    background: "#F8F9FA",
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
  },
  assignLabel: {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "20px",
    color: "#1A1E24",
    marginBottom: "12px",
  },
  assignRow: {
    display: "flex",
    gap: "14px",
    alignItems: "stretch",
    flexWrap: "wrap",
  },
  pickerWrap: {
    flex: 1,
    minWidth: "340px",
    maxWidth: "560px",
  },
  currentRole: {
    marginTop: "14px",
    paddingTop: "13px",
    borderTop: "1px solid #EEF1F6",
    display: "flex",
    alignItems: "center",
    gap: "9px",
  },
  currentRoleLabel: { fontWeight: 500, fontSize: "13px", lineHeight: "16px", color: "#A1ADBA" },
  noRoleText: { fontWeight: 500, fontSize: "13px", lineHeight: "16px", color: "#54606F" },
  chip: {
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    padding: "4px 11px",
    borderRadius: "20px",
    whiteSpace: "nowrap",
  },
  optionChip: {
    fontWeight: 600,
    fontSize: "11px",
    lineHeight: "14px",
    padding: "4px 9px",
    borderRadius: "20px",
    whiteSpace: "nowrap",
  },

  columns: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    alignItems: "start",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  column: {
    background: "#fff",
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    padding: "14px 18px",
    background: "#F3F5F7",
    borderBottom: "1px solid #E1E6F1",
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  iconBadge: {
    width: "30px",
    height: "30px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "none",
  },
  columnTitle: { fontWeight: 600, fontSize: "16px", lineHeight: "20px", color: "#1A1E24" },
  countPill: {
    marginLeft: "auto",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#54606F",
    background: "#fff",
    border: "1px solid #E1E6F1",
    borderRadius: "20px",
    padding: "2px 11px",
  },
  columnSearch: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 12px",
    borderBottom: "1px solid #EEF1F6",
    color: "#A1ADBA",
  },
  columnSearchInput: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "16px",
    color: "#1A1E24",
    "&::placeholder": { color: "#A1ADBA", fontWeight: 500 },
  },
  columnSearchClear: {
    width: "22px",
    height: "22px",
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: "6px",
    background: "transparent",
    color: "#A1ADBA",
    cursor: "pointer",
    transition: ".15s",
    "&:hover": { background: "#F3F5F7", color: "#54606F" },
  },
  list: { padding: "8px", minHeight: "300px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "11px 12px",
    borderRadius: "8px",
    transition: "background .15s",
    "&:hover": { background: "#F8F9FA" },
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowName: {
    display: "block",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "18px",
    color: "#5195E6",
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&:hover": { textDecoration: "underline" },
  },
  rowInn: { fontWeight: 500, fontSize: "12px", lineHeight: "15px", color: "#A1ADBA", marginTop: "3px" },
  removeBtn: {
    width: "30px",
    height: "30px",
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid transparent",
    borderRadius: "6px",
    background: "transparent",
    color: "#A1ADBA",
    cursor: "pointer",
    transition: ".15s",
    "&:hover:not(:disabled)": { background: "#FEF2F2", color: "#EE294C", borderColor: "#F5CAD1" },
    "&:disabled": { cursor: "default" },
  },

  integrationWrap: { position: "relative", flex: "none", width: "220px" },
  pillExternal: {
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    alignItems: "center",
    gap: "7px",
    border: "none",
    borderRadius: "20px",
    padding: "6px 9px 6px 11px",
    background: "#E9F2FC",
    color: "#5195E6",
    cursor: "pointer",
    transition: "background .15s",
    "&:hover": { background: "#D8E8FA" },
  },
  pillInternal: {
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    alignItems: "center",
    gap: "7px",
    border: "1px solid #E1E6F1",
    borderRadius: "20px",
    padding: "6px 9px 6px 11px",
    background: "#F3F5F7",
    color: "#54606F",
    cursor: "pointer",
    transition: "border-color .15s",
    "&:hover": { borderColor: "#A1ADBA" },
  },
  pillLabel: {
    flex: 1,
    minWidth: 0,
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pillLabelExternal: { fontWeight: 600, fontSize: "12px", lineHeight: "16px" },
  pillLabelInternal: { fontWeight: 500, fontSize: "12px", lineHeight: "16px" },

  overlay: { position: "fixed", inset: 0, zIndex: 30 },
  integrationMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    width: "max-content",
    background: "#fff",
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    boxShadow: "0 12px 32px rgba(16,24,40,.12)",
    zIndex: 40,
    overflow: "hidden",
    padding: "6px",
  },
  integrationHeading: {
    fontWeight: 600,
    fontSize: "11px",
    lineHeight: "14px",
    color: "#A1ADBA",
    letterSpacing: ".04em",
    textTransform: "uppercase",
    padding: "7px 10px 6px",
  },
  integrationOption: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "9px 10px",
    border: "none",
    background: "transparent",
    borderRadius: "7px",
    cursor: "pointer",
    textAlign: "left",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "16px",
    color: "#1A1E24",
    whiteSpace: "nowrap",
    "&:hover": { background: "#F3F5F7" },
  },
  integrationCheck: {
    width: "16px",
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const LeadRolesTab = () => {
  const classes = useStyles();

  const [customers, setCustomers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [pending, setPending] = useState(null);
  const [openIntegration, setOpenIntegration] = useState(null);
  const [genQuery, setGenQuery] = useState("");
  const [recQuery, setRecQuery] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [customersData, integrationsData] = await Promise.all([
          getAllCustomersWithClients(),
          getLeadIntegrations(),
        ]);
        setCustomers(customersData || []);
        setIntegrations(integrationsData || []);
      } catch (error) {
        console.error("[LeadRolesTab] load data", error);
      }
    };
    load();
  }, []);

  const generators = useMemo(
    () => customers.filter((c) => c.customerRole === ROLE_GENERATOR),
    [customers]
  );
  const receivers = useMemo(
    () => customers.filter((c) => c.customerRole === ROLE_RECEIVER),
    [customers]
  );

  const matchesQuery = (c, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${c.name || ""} ${c.externalId || ""}`.toLowerCase().includes(q);
  };
  const filteredGenerators = useMemo(
    () => generators.filter((c) => matchesQuery(c, genQuery)),
    [generators, genQuery]
  );
  const filteredReceivers = useMemo(
    () => receivers.filter((c) => matchesQuery(c, recQuery)),
    [receivers, recQuery]
  );

  const selectedFresh = selected
    ? customers.find((c) => c.id === selected.id) || selected
    : null;

  const changeRole = async (customer, role) => {
    if (!customer) return;
    setUpdatingId(customer.id);
    try {
      await updateCustomerLeadRole(customer.id, role);
      // Бэкенд сбрасывает интеграцию при смене роли — отражаем локально.
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customer.id ? { ...c, customerRole: role, leadIntegrationKey: null } : c
        )
      );
    } catch (error) {
      console.error("[LeadRolesTab] change role", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const changeIntegration = async (customer, key) => {
    setUpdatingId(customer.id);
    try {
      await updateCustomerLeadRole(customer.id, ROLE_RECEIVER, key);
      setCustomers((prev) =>
        prev.map((c) => (c.id === customer.id ? { ...c, leadIntegrationKey: key } : c))
      );
    } catch (error) {
      console.error("[LeadRolesTab] change integration", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const requestAssign = (role) => {
    if (!selectedFresh) return;
    setPending({ type: "role", customer: selectedFresh, role });
  };

  const requestRemove = (customer) => {
    setPending({ type: "role", customer, role: ROLE_STANDARD });
  };

  const pickIntegration = (customer, key) => {
    setOpenIntegration(null);
    if ((customer.leadIntegrationKey || null) === (key || null)) return;
    setPending({ type: "integration", customer, key });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const action = pending;
    setPending(null);
    if (action.type === "integration") {
      await changeIntegration(action.customer, action.key);
      return;
    }
    await changeRole(action.customer, action.role);
    if (action.role !== ROLE_STANDARD) {
      setSelected(null);
    }
  };

  const integrationName = (key) =>
    integrations.find((i) => i.key === key)?.name || INTERNAL_LABEL;

  const confirmProps = () => {
    if (!pending) return { title: "", text: "", confirmLabel: "" };
    const name = pending.customer.name;
    if (pending.type === "integration") {
      return {
        title: "Изменить интеграцию",
        text: `Назначить заказчику «${name}» интеграцию «${integrationName(pending.key)}»?`,
        confirmLabel: "Изменить",
      };
    }
    if (pending.role === ROLE_STANDARD) {
      return {
        title: "Убрать роль",
        text: `Убрать роль «${ROLE_LABELS[pending.customer.customerRole] || ""}» у заказчика «${name}»? Заказчик вернётся к стандартной роли.`,
        confirmLabel: "Убрать",
      };
    }
    return {
      title: "Назначить роль",
      text: `Назначить заказчику «${name}» роль «${ROLE_LABELS[pending.role]}»?`,
      confirmLabel: "Назначить",
    };
  };

  const renderRow = (c, isReceiver) => {
    const isExternal = !!c.leadIntegrationKey;
    return (
      <Box key={c.id} className={classes.row}>
        <div className={classes.rowMain}>
          <RouterLink className={classes.rowName} to={`/customer/details/${c.id}`}>
            {c.name || "—"}
          </RouterLink>
          {c.externalId && <div className={classes.rowInn}>ИНН {c.externalId}</div>}
        </div>

        {isReceiver && (
          <div className={classes.integrationWrap}>
            <button
              type="button"
              title="Изменить интеграцию"
              className={isExternal ? classes.pillExternal : classes.pillInternal}
              onClick={() =>
                setOpenIntegration((cur) => (cur === c.id ? null : c.id))
              }
            >
              {isExternal ? (
                <PowerIcon style={{ fontSize: 16, flexShrink: 0 }} />
              ) : (
                <SwapHorizIcon style={{ fontSize: 16, flexShrink: 0 }} />
              )}
              <span
                className={`${classes.pillLabel} ${
                  isExternal ? classes.pillLabelExternal : classes.pillLabelInternal
                }`}
              >
                {isExternal ? integrationName(c.leadIntegrationKey) : INTERNAL_LABEL}
              </span>
              <KeyboardArrowDownIcon style={{ fontSize: 16, flexShrink: 0, opacity: 0.7 }} />
            </button>

            {openIntegration === c.id && (
              <>
                <div className={classes.overlay} onClick={() => setOpenIntegration(null)} />
                <div className={classes.integrationMenu}>
                  <div className={classes.integrationHeading}>Интеграция</div>
                  {[{ key: null, name: INTERNAL_LABEL }, ...integrations].map((opt) => {
                    const active = (c.leadIntegrationKey || null) === (opt.key || null);
                    return (
                      <button
                        type="button"
                        key={opt.key || "internal"}
                        className={classes.integrationOption}
                        onClick={() => pickIntegration(c, opt.key)}
                      >
                        <span className={classes.integrationCheck}>
                          {active && <CheckIcon style={{ fontSize: 16, color: "#5195E6" }} />}
                        </span>
                        <span style={{ flex: 1 }}>{opt.name}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        <button
          type="button"
          title="Убрать роль"
          className={classes.removeBtn}
          disabled={updatingId === c.id}
          onClick={() => requestRemove(c)}
        >
          {updatingId === c.id ? <CircularProgress size={16} /> : <CloseIcon style={{ fontSize: 18 }} />}
        </button>
      </Box>
    );
  };

  const renderColumn = (title, icon, allItems, items, isReceiver, query, setQuery) => {
    const hasQuery = query.trim().length > 0;
    return (
      <div className={classes.column}>
        <div className={classes.columnHeader}>
          <span className={classes.iconBadge}>
            {icon}
          </span>
          <span className={classes.columnTitle}>{title}</span>
          <span className={classes.countPill}>
            {hasQuery ? `${items.length} / ${allItems.length}` : allItems.length}
          </span>
        </div>
        {allItems.length > SEARCH_THRESHOLD && (
          <div className={classes.columnSearch}>
            <SearchIcon style={{ fontSize: 18, flexShrink: 0 }} />
            <input
              type="text"
              className={classes.columnSearchInput}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по названию или ИНН…"
            />
            {hasQuery && (
              <button
                type="button"
                title="Очистить"
                className={classes.columnSearchClear}
                onClick={() => setQuery("")}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        )}
        <div className={classes.list}>
          {allItems.length === 0 ? (
            <Box className={classes.noRoleText} style={{ textAlign: "center", padding: "48px 20px", color: "#A1ADBA" }}>
              Нет заказчиков с ролью «{title}»
            </Box>
          ) : items.length === 0 ? (
            <Box className={classes.noRoleText} style={{ textAlign: "center", padding: "48px 20px", color: "#A1ADBA" }}>
              Ничего не найдено
            </Box>
          ) : (
            items.map((c) => renderRow(c, isReceiver))
          )}
        </div>
      </div>
    );
  };

  const selectedMeta = selectedFresh ? roleMeta(selectedFresh.customerRole) : null;

  return (
    <Box className={classes.container}>
      <Box className={classes.assignBar}>
        <div className={classes.assignLabel}>Назначить роль</div>
        <div className={classes.assignRow}>
          <div className={classes.pickerWrap}>
            <SearchableSelect
              options={customers}
              value={selectedFresh}
              onChange={setSelected}
              getOptionLabel={(c) =>
                c.externalId ? `${c.name} (ИНН ${c.externalId})` : c.name || ""
              }
              getOptionPrimary={(c) => c.name || "—"}
              getOptionSecondary={(c) => (c.externalId ? `ИНН ${c.externalId}` : "")}
              getSearchText={(c) => `${c.name || ""} ${c.externalId || ""}`}
              renderOptionRight={(c) => {
                const m = roleMeta(c.customerRole);
                if (!m) return null;
                return (
                  <span className={classes.optionChip} style={{ background: m.bg, color: m.fg }}>
                    {m.label}
                  </span>
                );
              }}
              placeholder="Поиск по названию или ИНН…"
              searchPlaceholder="Поиск по названию или ИНН…"
            />
          </div>

          <ButtonBB
            startIcon={<PersonSearchIcon style={{ color: "#43A56C", fontSize: 20 }} />}
            disabled={!selectedFresh || updatingId != null}
            onClick={() => requestAssign(ROLE_GENERATOR)}
          >
            Лидогенератор
          </ButtonBB>
          <ButtonBB
            startIcon={<AccountBalanceIcon style={{ color: "#5195E6", fontSize: 20 }} />}
            disabled={!selectedFresh || updatingId != null}
            onClick={() => requestAssign(ROLE_RECEIVER)}
          >
            Финансовая компания/Банк
          </ButtonBB>
        </div>

        {selectedFresh && (
          <div className={classes.currentRole}>
            <span className={classes.currentRoleLabel}>Текущая роль:</span>
            {selectedMeta ? (
              <span
                className={classes.chip}
                style={{ background: selectedMeta.bg, color: selectedMeta.fg }}
              >
                {ROLE_LABELS[selectedFresh.customerRole]}
              </span>
            ) : (
              <span className={classes.noRoleText}>не назначена · стандартный заказчик</span>
            )}
          </div>
        )}
      </Box>

      <div className={classes.columns}>
        {renderColumn(
          "Лидогенераторы",
          <PersonSearchIcon style={{ color: "#43A56C", fontSize: 20 }} />,
          generators,
          filteredGenerators,
          false,
          genQuery,
          setGenQuery
        )}
        {renderColumn(
          "Финансовые компании/Банки",
          <AccountBalanceIcon style={{ color: "#5195E6", fontSize: 20 }} />,
          receivers,
          filteredReceivers,
          true,
          recQuery,
          setRecQuery
        )}
      </div>

      <ConfirmModal
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={handleConfirm}
        {...confirmProps()}
      />
    </Box>
  );
};

export default LeadRolesTab;
